import type { FormEvent } from "react";
import { ArrowRight } from "lucide-react";
import { useT } from "../../lib/i18n";
import { cx } from "../../lib/cx";
import { dsCard, dsControl } from "./styles";

export type AskSuggestion = {
  id: string;
  label: string;
};

type Props = {
  title: string;
  meta?: string;
  suggestions: AskSuggestion[];
  value: string;
  placeholder: string;
  onChange: (value: string) => void;
  onSubmit: (value: string) => void;
  /**
   * Fill a height-bounded grid cell. Chips scroll; the send pill stays at the
   * bottom. Off everywhere the card is sized by its content.
   */
  fill?: boolean;
};

/** Suggestion chips fill the input. Amber round send sits on the pill. */
export function AskAI({
  title,
  meta,
  suggestions,
  value,
  placeholder,
  onChange,
  onSubmit,
  fill = false,
}: Props) {
  const t = useT();
  function submit(event: FormEvent) {
    event.preventDefault();
    const next = value.trim();
    if (!next) return;
    onSubmit(next);
  }
  return (
    <section
      data-ds=""
      data-testid="ds-ask"
      aria-label={title}
      className={cx(dsCard, "flex h-full flex-col", fill && "min-h-0 overflow-hidden")}
    >
      <header className="mb-ds-head flex shrink-0 items-baseline justify-between gap-2.5">
        <h2 className="m-0 font-ds text-ds-card font-semibold text-ds-ink">{title}</h2>
        {meta ? <span className="text-ds-meta font-normal text-ds-ink-3">{meta}</span> : null}
      </header>
      <div
        className={cx(
          "mb-2.5 flex flex-wrap gap-1.5",
          fill && "min-h-0 flex-auto content-start overflow-y-auto",
        )}
      >
        {suggestions.map((suggestion) => (
          <button
            key={suggestion.id}
            type="button"
            data-testid={`ds-ask-chip-${suggestion.id}`}
            className={cx(
              dsControl,
              "rounded-ds-pill bg-ds-glass-strong px-[13px] py-[7px] text-left text-ds-body font-normal text-ds-ink-2 hover:bg-ds-amber-soft hover:text-ds-on-amber",
            )}
            onClick={() => onChange(suggestion.label)}
          >
            {suggestion.label}
          </button>
        ))}
      </div>
      <form
        className={cx(
          "flex min-h-11 shrink-0 items-center gap-2 rounded-ds-pill bg-ds-glass-strong py-1 pr-1 pl-3",
          fill && "mt-auto",
        )}
        onSubmit={submit}
      >
        <input
          data-testid="ds-ask-input"
          className="m-0 min-w-0 flex-1 border-0 bg-transparent p-0 font-ds text-ds-body font-normal text-ds-ink outline-none placeholder:text-ds-ink-3"
          value={value}
          placeholder={placeholder}
          aria-label={placeholder}
          autoComplete="off"
          onChange={(event) => onChange(event.target.value)}
        />
        <button
          type="submit"
          data-testid="ds-ask-send"
          aria-label={t("ds.ask.send")}
          className={cx(
            dsControl,
            "grid size-[30px] shrink-0 place-items-center rounded-full bg-ds-amber p-0 text-ds-on-amber",
          )}
        >
          <ArrowRight className="size-3.5" aria-hidden />
        </button>
      </form>
    </section>
  );
}
